import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Clock, Users, BadgeIndianRupee, ExternalLink, Send, Loader2, ShieldCheck } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { api, inr, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function TaskDetails() {
  const { code } = useParams();
  const [task, setTask] = useState(null);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/tasks/${code}`).then((r) => setTask(r.data)).catch(() => setError("Task not found or no longer available."));
  }, [code]);

  const goSubmit = () => {
    if (user === false) navigate("/login", { state: { next: `/submit/${code}` } });
    else if (user) navigate(`/submit/${code}`);
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="task-not-found">
        <h1 className="text-2xl font-bold font-heading text-slate-900">{error}</h1>
        <Link to="/tasks" className="mt-4 inline-block text-blue-700 font-semibold">Back to tasks</Link>
      </div>
    );
  }
  if (!task) {
    return <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" data-testid="task-detail-loading" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <Link to="/tasks" data-testid="back-to-tasks-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800 mb-5">← Back to Tasks</Link>
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8 animate-fade-up" data-testid="task-detail-card">
        <div className="flex flex-col sm:flex-row sm:items-start gap-5">
          <img src={fileUrl(task.app_logo) || "https://ui-avatars.com/api/?name=" + encodeURIComponent(task.app_name) + "&background=2563EB&color=fff&size=96"} alt={task.app_name} data-testid="task-detail-logo" className="w-20 h-20 rounded-3xl object-cover border border-slate-200" />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900" data-testid="task-detail-name">{task.app_name}</h1>
              <StatusBadge status={task.status} testid="task-detail-status" />
            </div>
            <p className="text-sm font-medium text-slate-500 mt-1" data-testid="task-detail-id">{task.task_code}</p>
            <p className="mt-3 text-base text-slate-600 leading-relaxed">{task.description}</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          <div className="bg-blue-50 rounded-2xl p-4 text-center">
            <BadgeIndianRupee className="w-5 h-5 text-blue-600 mx-auto" />
            <p className="mt-1.5 text-lg sm:text-2xl font-bold text-blue-700 font-heading" data-testid="task-detail-reward">{inr(task.reward)}</p>
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Reward</p>
          </div>
          <div className="bg-slate-50 rounded-2xl p-4 text-center">
            <Clock className="w-5 h-5 text-slate-600 mx-auto" />
            <p className="mt-1.5 text-base sm:text-xl font-bold text-slate-800 font-heading" data-testid="task-detail-time">{task.estimated_time}</p>
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Est. Time</p>
          </div>
          <div className="bg-slate-50 rounded-2xl p-4 text-center">
            <Users className="w-5 h-5 text-slate-600 mx-auto" />
            <p className="mt-1.5 text-base sm:text-xl font-bold text-slate-800 font-heading" data-testid="task-detail-slots">{task.participants_remaining}</p>
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Slots Left</p>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8">
        <h2 className="text-xl font-bold font-heading text-slate-900">Task Instructions</h2>
        <ol className="mt-5 space-y-4" data-testid="task-instructions-list">
          {(task.instructions || []).map((step, i) => (
            <li key={i} className="flex gap-3.5" data-testid={`instruction-step-${i + 1}`}>
              <span className="shrink-0 w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-sm font-bold flex items-center justify-center">{i + 1}</span>
              <p className="text-base text-slate-700 leading-relaxed pt-0.5">{step}</p>
            </li>
          ))}
        </ol>
        {task.demo_screenshot ? (
          <div className="mt-6" data-testid="demo-screenshot-block">
            <h3 className="text-sm font-bold font-heading text-slate-900 uppercase tracking-wide">Sample Proof Screenshot</h3>
            <p className="text-xs text-slate-500 mt-1">Task complete karne ke baad aisa screenshot lekar submit karein:</p>
            <img src={fileUrl(task.demo_screenshot)} alt="Sample proof screenshot" data-testid="task-demo-screenshot" className="mt-3 max-h-96 rounded-2xl border border-slate-200 object-contain bg-slate-50" />
          </div>
        ) : null}
        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-4 flex gap-3">
          <ShieldCheck className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600 leading-relaxed">Share your genuine experience. Rewards are for completing the research task — not for a positive review or any specific rating.</p>
        </div>
        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <a href={task.play_store_url} target="_blank" rel="noopener noreferrer" data-testid="open-play-store-cta"
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-white bg-slate-900 hover:bg-slate-800 transition-colors shadow-sm">
            <ExternalLink className="w-4 h-4" /> OPEN GOOGLE PLAY STORE
          </a>
          <button onClick={goSubmit} disabled={user === null} data-testid="submit-task-cta" className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors shadow-sm">
            <Send className="w-4 h-4" /> SUBMIT TASK
          </button>
        </div>
      </div>
    </div>
  );
}
